import React, { createContext, FC, PropsWithChildren, useContext } from 'react';
import { noop, sortBy } from 'lodash';
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import { useToast } from '@/context/Toast';
import { ToastVariant } from '@/components/Common/Toast';
import routes from '@/routes';
import { isConfiguredEqual } from '@/utils/configurator';
import { WishlistItem } from '@/types/product';
import { useLocalStorage } from '@/hooks/useStorage';

type WishlistContext = {
  items: WishlistItem[];
  add: (item: WishlistItem) => void;
  update: (item: WishlistItem) => void;
  remove: (item: WishlistItem) => void;
  has: (item: WishlistItem) => boolean;
  reset: () => void;
};

const Context = createContext<WishlistContext>({
  items: [],
  add: noop,
  update: noop,
  remove: noop,
  has: () => false,
  reset: noop,
});

export const useWishlist = () => useContext(Context);

const WishlistProvider: FC<PropsWithChildren> = ({ children }) => {
  const { showMessage } = useToast();
  const [items = [], setItems] = useLocalStorage<WishlistItem[]>('wishlist', []);

  const getItem = (needle: WishlistItem) =>
    items.find((item) => isConfiguredEqual(needle, item));

  const update = (item: WishlistItem) =>
    setItems((prev) => {
      const unchanged = prev?.filter((current) => !isConfiguredEqual(item, current));
      const sorted = sortBy([...(unchanged ?? []), item], 'sku');
      return sorted.filter((current) => current.quantity > 0);
    });

  const add = (item: WishlistItem) => {
    const existingItem = getItem(item);
    const quantity = (existingItem?.quantity ?? 0) + item.quantity;
    showMessage(
      {
        text: i18n._(t`Your product has been successfully added to estimate!`),
        subtext: item.name,
        link: [routes.wishlist(), i18n._(t`View estimate`)],
        type: ToastVariant.Extended,
      },
      ToastVariant.Extended
    );
    return update({ ...item, quantity });
  };

  const remove = (item: WishlistItem) => {
    const existingItem = getItem(item);
    return existingItem ? update({ ...existingItem, quantity: 0 }) : null;
  };

  const has = (item: WishlistItem) =>
    items.some((current) => isConfiguredEqual(item, current));

  const reset = () => setItems([]);

  return (
    <Context.Provider
      value={{
        items,
        add,
        update,
        remove,
        has,
        reset,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default WishlistProvider;
